import React, { useRef, useState, useEffect } from 'react';
import { UploadedFile } from '../hooks/useFileUpload';
import { useClickOutside } from '../../../hooks/useClickOutside';
import { UPLOAD_ACCEPTED_FORMATS, UPLOAD_MAX_FILES, UPLOAD_MAX_MB } from '../constants';

interface FileUploadPanelProps {
  uploadedFiles: UploadedFile[];
  validationError: string;
  onUploadFiles: (files: File[]) => void;
  onDeleteFile: (fileId: string) => void;
  onClose: () => void;
}

const FILE_ICON: Record<string, { icon: string; color: string }> = {
  pdf:  { icon: 'fas fa-file-pdf',        color: '#DC2626' },
  docx: { icon: 'fas fa-file-word',       color: '#2563EB' },
  xlsx: { icon: 'fas fa-file-excel',      color: '#16A34A' },
  csv:  { icon: 'fas fa-file-csv',        color: '#0891B2' },
  pptx: { icon: 'fas fa-file-powerpoint', color: '#EA580C' },
  txt:  { icon: 'fas fa-file-alt',        color: '#6b7280' },
  jpg:  { icon: 'fas fa-file-image',      color: '#D946EF' },
  jpeg: { icon: 'fas fa-file-image',      color: '#D946EF' },
  png:  { icon: 'fas fa-file-image',      color: '#D946EF' },
};

const getFileStyle = (ext: string) =>
  FILE_ICON[ext.toLowerCase()] ?? { icon: 'fas fa-file', color: '#6b7280' };

const FileUploadPanel: React.FC<FileUploadPanelProps> = ({
  uploadedFiles,
  validationError,
  onUploadFiles,
  onDeleteFile,
  onClose,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const boxRef = useRef<HTMLButtonElement>(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const activeFiles = uploadedFiles.filter((f) => f.status === 'uploading' || f.status === 'error');
  const doneFiles = uploadedFiles.filter((f) => f.status === 'done');
  const isUploading = uploadedFiles.some((f) => f.status === 'uploading');

  // Close popup when clicking outside (the summary box that toggles it counts as inside)
  useClickOutside([popupRef, boxRef], () => setPopupOpen(false), popupOpen);

  // Close popup when all done files are deleted
  useEffect(() => {
    if (doneFiles.length === 0) setPopupOpen(false);
  }, [doneFiles.length]);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length > 0) onUploadFiles(files);
    e.target.value = '';
  };

  return (
    <div className={`file-upload-panel${collapsed ? ' file-upload-panel--collapsed' : ''}`}>
      {/* Header */}
      <div className="file-upload-header">
        <button
          type="button"
          className="file-upload-title file-upload-title--btn"
          onClick={() => setCollapsed((v) => !v)}
          aria-expanded={!collapsed}
          title={collapsed ? 'Expand' : 'Collapse'}
        >
          <i className="fas fa-paperclip" aria-hidden="true" />
          <span>File Upload</span>
          {doneFiles.length > 0 && (
            <span className="fu-count-badge">{doneFiles.length}</span>
          )}
        </button>
        <div className="file-upload-header-actions">
          <button
            type="button"
            className="file-upload-collapse-btn"
            onClick={() => setCollapsed((v) => !v)}
            aria-label={collapsed ? 'Expand' : 'Collapse'}
            title={collapsed ? 'Expand' : 'Collapse'}
          >
            <i className={`fas fa-chevron-${collapsed ? 'down' : 'up'}`} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="file-upload-close"
            onClick={onClose}
            aria-label="Close"
            title="Close"
          >
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        </div>
      </div>

      {!collapsed && <>
        {/* Choose Files button — uploads immediately on selection */}
        <div className="file-upload-actions">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={UPLOAD_ACCEPTED_FORMATS.map((ext) => `.${ext}`).join(',')}
            style={{ display: 'none' }}
            onChange={handleFileInputChange}
          />
          <button
            type="button"
            className="file-upload-btn file-upload-btn--choose"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
          >
            <i className="fas fa-folder-open" aria-hidden="true" />
            Choose Files
          </button>
        </div>

        {/* Format hint */}
        <div className="file-upload-hint">
          {UPLOAD_ACCEPTED_FORMATS.join(' · ')} &nbsp;·&nbsp; max {UPLOAD_MAX_FILES} files · {UPLOAD_MAX_MB} MB each
        </div>

        {/* Uploading / error rows */}
        {activeFiles.length > 0 && (
          <div className="file-upload-list">
            {activeFiles.map((f) => {
              const { icon, color } = getFileStyle(f.extension);
              return (
                <div key={f.localId} className={`file-item file-item--${f.status}`}>
                  <i className={`${icon} file-item__icon`} style={{ color }} aria-hidden="true" />
                  <span className="file-item__name" title={f.name}>{f.name}</span>
                  <span className="file-item__status">
                    {f.status === 'uploading' && (
                      <span className="file-item__status--uploading">
                        <span className="file-item__spinner" aria-hidden="true" />
                        Uploading…
                      </span>
                    )}
                    {f.status === 'error' && (
                      <span className="file-item__status--error" title={f.errorMessage}>
                        <i className="fas fa-exclamation-circle" aria-hidden="true" />{' '}
                        {f.errorMessage}
                      </span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Uploaded files compact box + popup */}
        {doneFiles.length > 0 && (
          <div className="fu-uploaded-section">
            <button
              ref={boxRef}
              type="button"
              className={`fu-uploaded-box${popupOpen ? ' open' : ''}`}
              onClick={() => setPopupOpen((v) => !v)}
              aria-expanded={popupOpen}
            >
              <div className="fu-icon-stack">
                {doneFiles.slice(0, 3).map((f, i) => {
                  const { icon, color } = getFileStyle(f.extension);
                  return (
                    <i
                      key={f.localId}
                      className={`${icon} fu-stack-icon`}
                      style={{ color }}
                      aria-hidden="true"
                    />
                  );
                })}
              </div>
              <span className="fu-uploaded-label">
                {doneFiles.length} file{doneFiles.length !== 1 ? 's' : ''} uploaded
              </span>
              <i
                className={`fas fa-chevron-${popupOpen ? 'down' : 'up'} fu-chevron`}
                aria-hidden="true"
              />
            </button>

            {popupOpen && (
              <div className="fu-popup" ref={popupRef} role="dialog" aria-label="Uploaded files">
                <div className="fu-popup-header">
                  <span>Uploaded files</span>
                  <button
                    type="button"
                    className="fu-popup-close"
                    onClick={() => setPopupOpen(false)}
                    aria-label="Close"
                  >
                    <i className="fas fa-times" aria-hidden="true" />
                  </button>
                </div>
                <div className="fu-popup-list">
                  {doneFiles.map((f) => {
                    const { icon, color } = getFileStyle(f.extension);
                    return (
                      <div key={f.localId} className="fu-popup-item">
                        <i className={`${icon} fu-popup-icon`} style={{ color }} aria-hidden="true" />
                        <span className="fu-popup-name" title={f.name}>{f.name}</span>
                        <button
                          type="button"
                          className="fu-popup-delete"
                          aria-label={`Delete ${f.name}`}
                          onClick={() => onDeleteFile(f.fileId)}
                        >
                          <i className="fas fa-times" aria-hidden="true" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Validation error */}
        {validationError && (
          <div className="file-upload-error" role="alert">
            <i className="fas fa-exclamation-triangle" aria-hidden="true" />
            {validationError}
          </div>
        )}
      </>}
    </div>
  );
};

export default FileUploadPanel;
